import React from 'react'
import { CalendarDays } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { cn } from '@/lib/utils'

interface EventEntry {
  id: number
  name: string
  startDate: string
  endDate: string
  impactLevel: string
  demandMultiplier: number
}

interface UpcomingEventsCardProps {
  events: EventEntry[]
}

const impactStyles: Record<string, { bg: string; text: string }> = {
  minor: { bg: 'bg-slate-100', text: 'text-slate-700' },
  moderate: { bg: 'bg-amber-100', text: 'text-amber-700' },
  major: { bg: 'bg-orange-100', text: 'text-orange-700' },
  extreme: { bg: 'bg-rose-100', text: 'text-rose-700' },
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()}–${e.toLocaleDateString('en-GB', opts)}`
  }
  return `${s.toLocaleDateString('en-GB', opts)} – ${e.toLocaleDateString('en-GB', opts)}`
}

function daysUntil(dateStr: string): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(dateStr + 'T00:00:00')
  const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff < 0) return 'Active now'
  return `In ${diff}d`
}

export default function UpcomingEventsCard({ events }: UpcomingEventsCardProps) {
  return (
    <DashboardCard
      title="Upcoming Events"
      description="Events affecting pricing"
      icon={<CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          'Enabled events in the next few weeks and how strongly each one is set to lift your rates.',
        learnMore: '/guides/events',
      }}
      padded={false}
    >
      {events.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No upcoming events</p>
          <p className="mt-1 text-xs text-muted-foreground">Events will appear here when configured</p>
        </div>
      ) : (
        <div className="divide-y divide-border/30">
          {events.map((event) => {
            const style = impactStyles[event.impactLevel] || impactStyles.minor
            return (
              <div key={event.id} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/20">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{event.name}</span>
                    <span className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold capitalize',
                      style.bg, style.text
                    )}>
                      {event.impactLevel}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDateRange(event.startDate, event.endDate)}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-medium text-muted-foreground">
                  {daysUntil(event.startDate)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </DashboardCard>
  )
}
